import cookie from "cookie";
import Cookie from "universal-cookie";
import { get } from "lodash";
import dateFormat from "date-fns/format";
import wretch from "wretch";
import { fetchAuthorDetails } from "./api";
import { getSections, getTags, getCommentCount, getReaderType, isPaid } from "./data/story";
import { getParentSection } from "./data/section";
import { STORY_TEMPLATES, PAGE_TYPE } from "./constants";
const universalCookie = new Cookie();

function getDeviceTrackerId() {
  let cookies = cookie.parse(document.cookie);
  return cookies["thinmint"];
}

function getContentSource(story) {
  return get(story, ["metadata", "story-attributes", "source", 0]);
}

function getNewsType(story) {
  // Short News: SN,
  // Else: N
  return get(story, ["metadata", "story-attributes", "shortnews", 0]) === "true" ? "SN" : "N";
}

function getContentPostedShift(story) {
  // Night Shift: NS,
  // General Shift: GS
  // Add from the story attributes for backward compatibility
  const newsDateFromMeta = get(story, ["metadata", "story-attributes", "newsdate", 0]);
  const createdAt = newsDateFromMeta ? new Date(newsDateFromMeta) : new Date(story["created-at"]);
  return createdAt.getHours() >= 21 || createdAt.getHours() <= 7 ? "NS" : "GS";
}

function getDimension4(pageType, { section, story, collection, entity, author }) {
  switch (pageType) {
    case PAGE_TYPE.HOME_PAGE:
      return "home";
    case PAGE_TYPE.SECTION_PAGE:
      return get(section, ["name"], "");
    case PAGE_TYPE.STORY_PAGE:
      return `${get(story, ["sections", 0, "name"], "")} | ${get(
        story,
        ["metadata", "story-attributes", "theme", 0],
        ""
      )} | ${getContentSource(story)} | ${getNewsType(story)} | ${getContentPostedShift(story)}`;
    case PAGE_TYPE.RASIPALAN_PAGE:
      return get(story, ["slug"], "Unknown");
    case PAGE_TYPE.GURUPEYARCHI_STORY_PAGE:
      return get(story, ["slug"], "Unknown");
    case PAGE_TYPE.GURUPEYARCHI_PAGE:
    case PAGE_TYPE.BUNDLE_PAGE:
      return collection.name;
    case PAGE_TYPE.TOPIC_PAGE:
      return `${get(entity, ["name"], "Unknown")} | ${get(entity, ["tamil-name"], "Unknown")}`;
    case PAGE_TYPE.AUTHOR_PAGE:
      return `${get(author, ["metadata", "author_name_en"], "Unknown")} | ${get(author, ["name"], "Unknown")}`;
  }
}

function getDimension5({ story, section, collection, entity, author }) {
  if (!story && !section && !collection && !entity && !author) {
    return "Unknown";
  }
  if (entity) {
    return `${get(entity, ["entity-type-id"], "Unknown")}-${get(entity, ["id"], "Unknown")}`;
  }

  if (author) {
    return get(author, ["id"], "Unknown");
  }

  if (!story && collection) {
    // magazine landing page
    const magazineEntity = get(collection, ["metadata", "entities", "collectionEntities", "magazine", 0]);
    if (magazineEntity) {
      return `${magazineEntity.id}-${collection.id}`;
    } else {
      return collection["id"];
    }
  }

  section = section || get(story, ["sections", 0]);

  if (section) {
    // article or section pages
    const parentSection = getParentSection(section);
    let sections = [section];
    if (parentSection) {
      sections.unshift(parentSection);
    }

    return sections.map(section => section["name"]).join("-");
  }

  return null;
}

export const GA = {
  inited: false,
  loadScript(cb) {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.defer = true;
    const node = document.getElementsByTagName("script")[0];
    global.qtInitialGA = function() {
      global.ga("create", global.qtConfig.integrations.gaKey, "auto");
      cb();
    };
    script.onload = global.qtInitialGA;
    script.src = `https://www.google-analytics.com/analytics.js`;
    node.parentNode.insertBefore(script, node);
    global["GoogleAnalyticsObject"] = "ga";
  },
  registerPageView(pageType, { section, story, collection, entity, author, infiniteTagRequired } = {}) {
    try {
      if (!this.inited) {
        this.loadScript(registerPageViewImpl.bind(this));
        this.inited = true;
        return;
      }
      registerPageViewImpl.call(this);
    } catch (e) {
      console.error(e);
    }

    function triggerPageView(user) {
      global.ga(function(tracker) {
        var clientId = tracker.get("clientId");
        if (clientId === "") {
          clientId = "Unknown";
        }
        global.ga("set", "dimension1", user.userType || "Unknown");
        global.ga("set", "dimension2", user.userId || "Unknown");
        global.ga("set", "dimension3", getDeviceTrackerId() || "Unknown");
        global.ga(
          "set",
          "dimension4",
          getDimension4(pageType, { section, story, collection, entity, author }) || "Unknown"
        );
        global.ga("set", "dimension5", getDimension5({ section, story, collection, entity, author }) || "Unknown");
        global.ga("set", "dimension6", clientId);
        global.ga("set", "dimension8", get(story, ["story-template"], "Unknown")); // dimension7 is used for test purpose
        const isMagazineIssue = !!get(collection, ["metadata", "entities", "collectionEntities", "magazine"], [])
          .length;
        if (isMagazineIssue) {
          global.ga("set", "dimension9", collection["magazineEnName"] || "Unknown");
          const issueDate = new Date(collection["collection-date"] || null);
          const issueDateStringified = dateFormat(issueDate, "D-MMMM-YYYY");
          global.ga("set", "dimension10", issueDateStringified, "Unknown");
        } else {
          global.ga("set", "dimension9", "Unknown");
          global.ga("set", "dimension10", "Unknown");
        }
        if (infiniteTagRequired) {
          global.ga("set", "dimension11", "infinite");
        } else {
          global.ga("set", "dimension11", "Unknown");
        }

        if (global.ccaud) {
          global.ga("set", "dimension12", global.ccaud.getProfileId());
        }

        let cookies = cookie.parse(document.cookie);
        if (cookies["story_c"]) {
          global.ga("set", "dimension13", cookies["story_c"]);
        }

        if (pageType === PAGE_TYPE.STORY_PAGE) {
          global.ga("set", "dimension14", get(story, ["id"]));
          global.ga("set", "dimension15", get(story, ["author-name"]));
          const deepbi = universalCookie.get("deepbi_segment");
          global.ga("set", "dimension16", get(deepbi, ["testgroup"], null));
        }
        global.ga("send", "pageview", {
          page: `${window.location.pathname}${window.location.search}`,
          hitCallback: () => {
            if (user && user.userId && global.navigator && global.navigator.sendBeacon) {
              global.navigator.sendBeacon(`/api/vikatan/v1/ping-user/ga/${user.userId}`);
            }
          }
        });
      });
    }
    function registerPageViewImpl() {
      if (!global.ga) {
        return;
      }
      if (global.userPromise) {
        global.userPromise.catch(() => ({})).then(user => {
          triggerPageView(user || {});
        });
      } else {
        triggerPageView({});
      }
    }
  },
  customEvent(category, action, label) {
    global.ga && global.ga("send", "event", category, action, label);
  }
};

export const ContentInsights = {
  loadScript() {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.defer = true;
    script.onload = () => {
      global._ain.track();
    };
    script.src =
      document.location.protocol === "https:"
        ? "https://d7d3cf2e81d293050033-3dfc0615b0fd7b49143049256703bfce.ssl.cf1.rackcdn.com/stf.js"
        : "http://t.contentinsights.com/stf.js";
    const node = document.getElementsByTagName("script")[0];
    node.parentNode.insertBefore(script, node);
  },
  registerNonStoryPageView(contentInsightsId, pageType, { collection, section }) {
    global.userPromise.catch(() => ({})).then(user => {
      const data = {
        id: contentInsightsId,
        reader_type: getReaderType(user.userType),
        postid: (section && section.id) || collection.id,
        maincontent: `.js-content-insights-${(section && section.id) || collection.id}`,
        title: (section && section.name) || collection.name
      };
      data.url = global.location.href || "Unknown";
      if (pageType === PAGE_TYPE.HOME_PAGE) {
        data.sections = "home";
      } else if (pageType === PAGE_TYPE.SECTION_PAGE) {
        data.url = section && section.url ? section.url : data.url;
        data.sections = (section && section.name) || (collection && collection.name) || "Unknown";
      } else if (collection) {
        if (pageType === PAGE_TYPE.MAGAZINE_PAGE) {
          data.sections = `${collection["magazineEnName"]} - Issue Date - ${dateFormat(
            collection["collection-date"],
            "DD-MMMM-YYYY"
          )}`;
        } else {
          const section = get(collection, ["metadata", "section", "0"]);
          if (section) {
            data.url = section.url;
          }
          data.sections = (section && section.name) || collection.name;
        }
      }
      global._ain = { ...global._ain, ...data };

      if (global._ain.track) {
        global._ain.track();
      } else {
        this.loadScript();
      }
    });
  },
  registerPageView(contentInsightsId, story, metypeConfig) {
    let wordCount;
    try {
      wordCount = document.querySelector(`.js-content-insights-${story.id} .js-story`).textContent.split(/\s+/).length;
    } catch (e) {
      console.error("Error calculating word count for content insight");
    }
    Promise.all([global.userPromise.catch(() => ({})), getCommentCount(metypeConfig), fetchAuthorDetails(story)]).then(
      ([user, commentCount, author]) => {
        const data = {
          id: contentInsightsId,
          url: story.url,
          postid: get(story, ["id"], ""),
          maincontent: `.js-content-insights-${story.id} .js-story`,
          title: get(story, ["headline"], ""),
          pubdate: get(story, ["last-published-at"]) ? new Date(get(story, ["last-published-at"])).toISOString() : "",
          authors: get(author, ["metadata", "nickname_en"]) || get(author, ["metadata", "nickname_ta"]) || author.name,
          sections: getSections(story),
          tags: getTags(story),
          comments: commentCount,
          reader_type: getReaderType(user.userType),
          access_level: isPaid(story) ? "paid" : "free",
          word_count: wordCount
        };
        global._ain = { ...global._ain, ...data };

        if (global._ain.track) {
          global._ain.track();
        } else {
          this.loadScript();
        }
      }
    );
  }
};

export const ComScore = {
  inited: false,
  loadScript() {
    global._comscore = global._comscore || [];
    global._comscore.push({ c1: "2", c2: "20272655" });
    (function() {
      var s = document.createElement("script");
      var el = document.getElementsByTagName("script")[0];
      s.defer = true;
      s.src =
        (document.location.protocol === "https:" ? "https://sb" : "http://b") + ".scorecardresearch.com/beacon.js";
      el.parentNode.insertBefore(s, el);
    })();
  },
  registerPageView() {
    try {
      if (!this.inited) {
        this.loadScript();
        this.inited = true;
      } else {
        global.COMSCORE && global.COMSCORE.beacon({ c1: "2", c2: "20272655" });
        wretch("/v/api/comscore")
          .headers({ "Content-Type": "application/json" })
          .get();
      }
    } catch (e) {
      console.error(e);
    }
  }
};

export const RazorPay = {
  inited: false,
  loadScript() {
    if (this.inited) {
      return;
    }
    let e = window.document.createElement("script");
    e.type = "text/javascript";
    e.charset = "utf-8";
    e.setAttribute("crossorigin", "anonymous");
    e.id = "razorpay";
    e.defer = true;
    e.src = "https://checkout.razorpay.com/v1/checkout.js";
    let t = window.document.getElementsByTagName("head")[0];
    t.appendChild(e);
    this.inited = true;
  }
};

export const currentStory = {
  storyCookie(story, testgroup = "") {
    let cookies = new Cookie();
    cookies.set("currentStory", JSON.stringify({ id: story.id, testgroup: testgroup }), {
      path: "/",
      domain: ".vikatan.com"
    });
  },
  setStorydata(story, issueCollection) {
    let storyinfo = isPaid(story) ? "P" : "F";
    const sections = [get(story, ["sections", 0], {})];
    const parentSection = getParentSection(sections[0]);
    if (parentSection) {
      sections.unshift(parentSection);
    }

    let paywallobj = {
      ArticleTitle: story.headline,
      ArticleID: story.id,
      Category: get(sections, [0, "name"]) || "",
      SubCategory: get(sections, [1, "name"]) || "",
      ArticleType: storyinfo
    };

    if (storyinfo === "P") {
      if (issueCollection) {
        const issueDate = new Date(issueCollection["collection-date"] || null) || null;
        const issueDateStringified = dateFormat(issueDate, "D-MMMM-YYYY");
        paywallobj.IssueDate = issueDateStringified || "";
        paywallobj.Magazine = get(issueCollection, ["magazineEnName"]);
      }
    }
    paywallobj.Path = get(story, ["url"]);
    this.story = paywallobj;
  },
  getStorydata() {
    return this.story;
  },
  story: false
};

export const DeepBI = {
  inited: false,
  paywallStory(story, issueCollection, paywallType, testgroup = "", evnames = false) {
    try {
      let gaInitializerListenerId = setInterval(function() {
        if (global.ga) {
          clearInterval(gaInitializerListenerId);
          paywallGAEvent();
        }
      });

      let deepInitializerListenerId = setInterval(function() {
        if (global.deep) {
          clearInterval(deepInitializerListenerId);
          paywallEvents();
        }
      });

      paywallWE();
    } catch (e) {
      console.error(e);
    }

    evnames = `${evnames}_paywall_displayed`;

    function paywallEvents() {
      global.deep.event({
        "event.type": evnames,
        "paywall type": paywallType,
        vikGroup: (global && global.paywallPromise.type) || "A",
        testgroup: testgroup,
        "paywall-displayedStory": {
          template: story["story-template"],
          title: story.headline,
          slug: story.slug,
          subtitle: story["sub-headline"],
          webtitle: document.title,
          id: story.id,
          publication: {
            date: dateFormat(story["published-at"], "YYYY-MM-DD"),
            ts: story["published-at"]
          },
          ArticleType: isPaid(story) ? "Paid" : "Free",
          tags: story.tags.map(tag => tag.name)
        }
      });
    }

    function paywallGAEvent() {
      global.ga("send", "event", evnames, paywallType, "P");
    }

    function paywallWE() {
      if (global.webengage) {
        global.userPromise.catch(() => null).then(user => {
          let cookies = new Cookie();
          let storyinfo = isPaid(story) ? "P" : "F";
          const sections = [get(story, ["sections", 0], {})];
          const parentSection = getParentSection(sections[0]);
          if (parentSection) {
            sections.unshift(parentSection);
          }

          let paywallobj = {
            PaywallType: paywallType,
            ArticleTitle: story.headline,
            ArticleID: story.id,
            Category: get(sections, [0, "name"]) || "",
            SubCategory: get(sections, [1, "name"]) || "",
            ArticleType: storyinfo
          };

          if (storyinfo === "P") {
            const issueDate = new Date(issueCollection["collection-date"] || null) || null;
            const issueDateStringified = dateFormat(issueDate, "D-MMMM-YYYY");
            paywallobj.ArticleType = storyinfo;
            paywallobj.Magazine = issueCollection["magazineEnName"] || "";
            paywallobj.IssueDate = issueDateStringified || "";
          }

          global.webengage.track(evnames, paywallobj);
          cookies.set("paywallStory", JSON.stringify({ id: story.id, testgroup: testgroup }), {
            path: "/",
            domain: ".vikatan.com"
          });
        });
      }
    }
  },
  paywallEventTrigger(eventType, args = {}) {
    let deepInitializerListenerId = setInterval(function() {
      if (global.deep) {
        clearInterval(deepInitializerListenerId);
        let storyInfo = currentStory.getStorydata();
        global.deep.event(
          Object.assign(
            {
              "event.type": eventType,
              paywallStory: storyInfo && JSON.stringify(storyInfo)
            },
            args
          )
        );
      }
    });
  },
  WebengageUserId() {
    let WeInitializerListenerId = setInterval(function() {
      if (typeof global.webengage.user.getAnonymousId === "function") {
        clearInterval(WeInitializerListenerId);
        global.deep &&
          global.deep({
            anonymous_id: global.webengage.user.getAnonymousId()
          });
      }
    });
  },
  setCookie() {
    global.deep &&
      global.deep({
        vikatan: {
          cookie: getDeviceTrackerId()
        }
      });
  },
  setMemberData() {
    global.userPromise
      .then(user => {
        global.deep &&
          global.deep({
            vikatan: {
              customer: {
                ...user,
                loggedIn: true
              }
            }
          });
      })
      .catch(() => {
        global.deep &&
          global.deep({
            vikatan: {
              customer: {
                loggedin: false
              }
            }
          });
      });
  },
  setInitialData() {
    this.setCookie();
    this.setMemberData();
    this.WebengageUserId();
  },
  loadScript(f) {
    /* Deep Tracker (c) 2019-01-28 v3.0.6 */
    let e = window.document.createElement("script");
    e.type = "text/javascript";
    e.charset = "utf-8";
    e.setAttribute("crossorigin", "anonymous");
    e.defer = true;
    e.src = (window.document.location.protocol || "http:") + "//api.deep.bi/v3/sizzle";
    let t = window.document.getElementsByTagName("head")[0];
    t.appendChild(e);
    e = window.document.createElement("script");
    e.type = "text/javascript";
    e.charset = "utf-8";
    e.setAttribute("crossorigin", "anonymous");
    e.defer = true;
    e.src = (window.document.location.protocol || "http:") + "//api.deep.bi/v3/init";
    t.appendChild(e);
    let deepInitializerListenerId = setInterval(
      function() {
        if (window.hasOwnProperty("deepTracker") && window.deepTracker && window.deepTracker.isInitialized()) {
          clearInterval(deepInitializerListenerId);
          window.deep = window.deepTracker.track("17cZaviwFrV2", "Da16NqKwj2619hxwdhdGH9u1");
          window.deep.options.trackconsent = true;
          window.deep.options.dntignore = true;
          window.deep.options.media = true;
          this.setInitialData();
          f && f();
          window.deep();
          this.inited = true;
        }
      }.bind(this)
    );
  },
  registerPageView({ story, section } = {}) {
    if (story) {
      currentStory.story = story;
    }
    try {
      if (!this.inited) {
        this.loadScript(registerPageViewImpl.bind(this));
        return;
      } else {
      }
      registerPageViewImpl();
    } catch (e) {
      console.error(e);
    }

    function registerPageViewImpl() {
      let stotyData = {};
      let pageopen = "";
      let lotamePersona = "";

      if (global.ccaud && global.ccaud.getAudiences().length > 0) {
        let ccaudWeCat = [];
        for (let ccaudForId = 0; ccaudForId < global.ccaud.getAudiences().length; ccaudForId++) {
          let match = global.ccaud.getAudiences()[ccaudForId].toLowerCase();
          if (match.includes("aud")) {
            ccaudWeCat.push(match.replace(/_[0-9]{6}$/gim, ""));
          }
        }
        lotamePersona = String(ccaudWeCat);
      }

      let cookies = cookie.parse(document.cookie);
      pageopen = {
        pagepath: window.location.href,
        pagetitle: window.document.title,
        pagepersona: lotamePersona || ""
      };
      global.deep.event({
        "event.type": "page-open",
        "page.path": global.location.href,
        "page.title": global.document.title,
        "page.persona": lotamePersona || "",
        "page.variant": cookies["story_c"] ? cookies["story_c"] : ""
      });

      if (story && window.location.pathname !== "/story-feed") {
        const sections = [get(story, ["sections", 0], {})];
        const parentSection = getParentSection(sections[0]);
        if (parentSection) {
          sections.unshift(parentSection);
        }
        stotyData = {
          template: story["story-template"],
          title: story.headline,
          slug: story.slug,
          subtitle: story["sub-headline"],
          webtitle: document.title,
          id: story.id,
          publication: {
            date: dateFormat(story["published-at"], "YYYY-MM-DD"),
            ts: story["published-at"]
          },
          ArticleType: isPaid(story) ? "Paid" : "Free",
          tags: story.tags.map(tag => tag.name),
          variant: cookies["story_c"] ? cookies["story_c"] : ""
        };

        fetchAuthorDetails(story).then(author => {
          let data = {
            article: stotyData,
            categories: sections.map(section => section["name"]),
            author: {
              name: [
                get(author, ["metadata", "nickname_en"]) || get(author, ["metadata", "nickname_ta"]) || author.name
              ]
            }
          };

          const issueCollection = get(story, ["collections"], []).find(collection =>
            get(collection, ["metadata", "entities", "collectionEntities", "magazine", 0, "id"])
          );
          if (issueCollection) {
            data = {
              ...data,
              issue: {
                id: issueCollection.id,
                publication: {
                  date: dateFormat(issueCollection["collection-date"], "YYYY-MM-DD"),
                  ts: issueCollection["collection-date"]
                }
              }
            };
          }
          global.deep && global.deep({ vikatan: data });
          global.deep.addElements([
            { type: "main-container", selector: ".js-story-main-container" },
            { type: "article", selector: ".js-story" },
            { type: "related", selector: ".js-related-stories" },
            { type: "article-header", selector: ".js-story-header" },
            { type: "article-footer", selector: ".js-story-footer" }
          ]);
          global.deep.options.scrolldepth = {
            elements: [{ type: "main-container" }, { type: "article" }]
          };
          global.deep.options.viewability = {
            elements: [{ type: "related" }, { type: "article-header" }, { type: "article-footer" }]
          };
        });
        // Coin Ping
        story &&
          global.userPromise.then(user => {
            var customer = {
              userId: user.userId,
              username: user.username,
              userType: user.userType,
              userEmail: user.userEmail,
              userJoinDate: user.userJoinDate,
              lpId: (get(global, ["ccaud"]) && global.ccaud.getProfileId()) || ""
            };
            var userSess;
            if (get(global, ["webengage", "state", "getSession"])) {
              userSess = global.webengage.state.getSession();
              Object.assign(customer, {
                ip: get(userSess, ["ip"]),
                city: get(userSess, ["we_city"]),
                country: get(userSess, ["we_country"])
              });
            }

            let sections = [get(story, ["sections", 0], {})];
            global.fetch(`https://ping.vikatan.com/?userid=${user.userId}&aid=${story.id}`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json"
              },
              body: JSON.stringify({
                data: {
                  user: {
                    cookie: getDeviceTrackerId(),
                    customer: customer
                  },
                  event: {
                    type: "ArticleView",
                    timestamp: Date.now(),
                    eventattribute: {
                      AID: story.id,
                      Atitle: story.headline,
                      url: global.location.href,
                      path: story.slug,
                      section: section && get(sections, [0, "name"]),
                      SubSection: section && get(sections, [1, "name"]),
                      domain: window.location.hostname
                    }
                  },
                  device: {
                    devicetype: getDeviceType(),
                    CookieID: getDeviceTrackerId(),
                    Platform: getOS(),
                    lpId: (get(global, ["ccaud"]) && global.ccaud.getProfileId()) || "",
                    ip: get(userSess, ["ip"]),
                    city: get(userSess, ["we_city"]),
                    country: get(userSess, ["we_country"])
                  }
                }
              })
            });
          });
      }

      global.deep &&
        global.deep.event({
          "event.type": "page-view",
          pv: {
            ...pageopen,
            ...stotyData
          }
        });

      if (section) {
        let sections = [section];
        const parentSection = getParentSection(section);
        if (parentSection) {
          sections.unshift(parentSection);
        }

        global.deep &&
          global.deep({
            vikatan: {
              categories: sections.map(section => section["name"])
            }
          });
      }
    }
  },
  customEvent(type, label) {
    global.deep.event({
      "event.type": "article-share",
      "social.action": "share",
      "story.id": label,
      "social.click": type
    });
  },
  homepageEvent(homeData) {
    if (!this.inited) {
      this.loadScript(registerHomePageViewImpl.bind(this));
    } else {
      registerHomePageViewImpl();
    }
    function registerHomePageViewImpl() {
      global.deep.event({
        "event.type": "page-view",
        pv: {
          pagepath: get(homeData, ["data", "vikcurrentHost", "host-url"], null),
          pagetitle: get(homeData, ["title"], null)
        }
      });
    }
  },
  triggerEvent(type, args = {}) {
    try {
      if (!this.inited) {
        this.loadScript(ce.bind(this));
        return;
      }
      ce.call(this);
    } catch (e) {
      console.error(e);
    }
    function ce() {
      global.deep &&
        global.deep.event(
          Object.assign(
            {
              "event.type": type
            },
            args
          )
        );
    }
  }
};

export const WebEngage = {
  userRegistered: false,
  inited: false,
  sjFlag: false,
  loadScript() {
    this.inited = true;
  },
  registerUser() {
    if (!global.webengage || this.userRegistered) {
      return;
    }
    global.userPromise
      .then(user => {
        const { userId, username, userEmail, userMobileNumber } = user;
        global.webengage.user.setAttribute("Cookie_Id", getDeviceTrackerId());
        // If User logged In pass below attributes
        global.webengage.user.setAttribute("User_Id", userId);
        global.webengage.user.login(userId);
        global.webengage.user.setAttribute("we_first_name", username);
        global.webengage.user.setAttribute("we_email", userEmail);
        userMobileNumber && global.webengage.user.setAttribute("we_phone", userMobileNumber);
      })
      .catch(() => {
        console.warn("User attribution error occured");
      });
    this.userRegistered = true;
  },
  registerPageView({ story, section, triggerArticleEvent = true } = {}) {
    try {
      if (!this.inited) {
        window.webengageRegisterPageView = registerPageViewImpl.bind(this);
        this.loadScript();
        registerPageViewImpl.call(this);
        return;
      }

      registerPageViewImpl.call(this);
    } catch (e) {
      console.error(e);
    }

    function registerPageViewImpl() {
      const current = new Date();
      const nextWeek = new Date();
      nextWeek.setDate(current.getDate() + 28);
      if (!global.webengage) {
        return;
      }
      this.registerUser();

      if (global.ccaud && global.ccaud.getAudiences().length > 0) {
        let ccaudWeCat = [];
        for (let ccaudForId = 0; ccaudForId < global.ccaud.getAudiences().length; ccaudForId++) {
          let smatch = global.ccaud.getAudiences()[ccaudForId].toLowerCase();
          if (smatch.includes("aud")) {
            let out = smatch.match(/[0-9]{6}$/gim);
            out && ccaudWeCat.push(out[0]);
          }
        }
        global.webengage.user.setAttribute("action", String(ccaudWeCat));
      }

      if (
        window.hasOwnProperty("deepTracker") &&
        window.deepTracker &&
        window.deepTracker.isInitialized() &&
        typeof window.deepTracker.getScore === "function"
      ) {
        window.deepTracker.getScore({ force: true, type: "profile" }).then(score => {
          score &&
            Object.keys(score).length !== 0 &&
            global.webengage.user.setAttribute({
              dbi_rfv_segment: score.rfv_segment,
              dbi_rfv_score: score.rfv_score,
              dbi_rfv_level: score.rfv_level,
              dbi_recency_score: score.recency_score,
              dbi_recency_level: score.recency_level,
              dbi_volume_level: score.volume_level,
              dbi_volume_score: score.volume_score,
              dbi_frequency_level: score.frequency_level,
              dbi_frequency_score: score.frequency_score,
              dbi_group: score.testgroup,
              P2S: score.p2s
            });

          !this.sjFlag &&
            score &&
            score.rfv_segment === "Addicted" &&
            global.userPromise.catch(() => null).then(user => {
              !universalCookie.get("sjflag") &&
                global
                  .wretch(`https://store.vikatan.com/api/premium/${universalCookie.get("vuid")}`)
                  .get()
                  .json(function(res) {
                    !res.show &&
                      universalCookie.set("sjflag", 1, {
                        path: "/",
                        domain: ".vikatan.com",
                        expires: new Date(Number(`${res.premium_end}999`))
                      });
                  });
              this.sjFlag = true;
            });

          score &&
            universalCookie.set(
              "deepbi_segment",
              { testgroup: score.testgroup, p2s: score.p2s },
              {
                domain: ".vikatan.com",
                expires: nextWeek,
                path: "/"
              }
            );
        });
      }

      global.webengage.track("Page View", {
        PageTitle: global.document.title,
        PagePath: global.location.href
      });

      let cookies = cookie.parse(document.cookie);
      if (cookies["vusr_unique_views"] !== "1") {
        let d = new Date();
        d.setUTCHours(23, 59, 59, 999);
        let cookiesnew = new Cookie();
        cookiesnew.set("vusr_unique_views", "1", { path: "/", expires: d, domain: ".vikatan.com" });
        global.webengage.track("Unique Visit", {
          PageTitle: global.document.title,
          PagePath: global.location.href
        });
      }

      let type = "Article View";
      if (story && triggerArticleEvent) {
        if (story["story-template"] === STORY_TEMPLATES.VIDEO) {
          type = "Video";
        }
        if (story["story-template"] === STORY_TEMPLATES.PHOTO_ALBUM) {
          type = "Album";
        }

        const sections = [get(story, ["sections", 0], {})];
        const parentSection = getParentSection(sections[0]);
        if (parentSection) {
          sections.unshift(parentSection);
        }

        fetchAuthorDetails(story).then(author => {
          global.webengage.track(type, {
            ArticleTitle: story.headline,
            ArticleID: story.id,
            Category: get(sections, [0, "name"]),
            SubCategory: get(sections, [1, "name"]),
            Author: get(author, ["metadata", "nickname_en"]) || get(author, ["metadata", "nickname_ta"]) || author.name,
            ArticleType: isPaid(story) ? "Paid" : "Free",
            tags: story.tags.map(tag => tag.name).join(","),
            Variant: cookies["story_c"] ? cookies["story_c"] : ""
          });
        });
      }

      if (section) {
        let sections = [section];
        const parentSection = getParentSection(section);
        if (parentSection) {
          sections.unshift(parentSection);
        }

        window.webengage &&
          window.webengage.track("Category View", {
            Category: get(sections, [0, "name"])
          });
      }
    }
  },
  pwEvent(ev, args) {
    try {
      if (!this.inited) {
        this.loadScript(triggerEv.bind(this));
        return;
      }
      triggerEv.call(this);
    } catch (e) {
      console.error(e);
    }

    function triggerEv() {
      window.webengage && window.webengage.track(ev, args);
    }
  }
};

export const Lotame = {
  scriptLoaded: false,

  loadScript(onload) {
    let script1, script2;
    if (!this.scriptLoaded) {
      script2 = document.createElement("script");
      const lotameClientId = 9593;
      window.lotameClientId = lotameClientId;
      script2.innerHTML = `
      ! function() {
        var lotameClientId = '${lotameClientId}';
        var audLocalStorageKey = 'lotame_' + lotameClientId + '_auds';
        var lotameTagInput = {
            config: {
              clientId: Number(lotameClientId),
              audienceLocalStorage: audLocalStorageKey,
              onProfileReady: function(profile) {
                if(global) {
                  global.ccaud = profile;
                }
              }
            }
        };
        // Lotame initialization
        var lotameConfig = lotameTagInput.config || {};
        var namespace = window['lotame_' + lotameConfig.clientId] = {};
        namespace.config = lotameConfig;
        namespace.data = lotameTagInput.data || {};
        namespace.cmd = namespace.cmd || [];
      } ();
    `;
      script1 = document.createElement("script");
      script1.type = "text/javascript";
      script1.defer = true;
      script1.onload = onload;
      script1.src = "https://tags.crwdcntrl.net/lt/c/9593/lt.min.js";
      const node = document.getElementsByTagName("script")[0];
      node.parentNode.insertBefore(script2, node);
      node.parentNode.insertBefore(script1, node);
      this.scriptLoaded = true;
    } else {
      onload();
    }
  },

  registerPageView({ story = "", sections = "" } = {}) {
    try {
      sections = story ? story.sections : sections;
      const websiteUrl = get(global.qtConfig, ["sketches-host"]) || "";
      var shareSection = "";
      if (sections && sections.length > 0) {
        const childSection = sections[sections.length - 1];
        shareSection = get(childSection, ["name"], "");
      }
      this.loadScript(() => {
        if (!global.lotame_9593) {
          return;
        }
        global.userPromise.catch(() => null).then(user => {
          var lotameInt = [`site:${websiteUrl.replace(/^https?:\/\/www\./, "")}`, "Web", `${shareSection}`];
          if (user) {
            lotameInt.push("Registered");
            lotameInt.push(user.userType);
          }
          global.lotame_9593.cmd.push(function() {
            global.lotame_9593.page({
              behaviors: {
                int: lotameInt
              }
            });
          });
        });
      });
    } catch (e) {
      console.error(e);
    }
  }
};

export const LoadVuukle = {
  scriptLoaded: false,
  userRegistered: false,
  registerUser() {
    if (this.userRegistered) {
      return;
    }
    global.userPromise
      .then(user => {
        const { username, userEmail } = user;
        this.userRegistered = true;
        isNaN(userEmail) &&
          wretch(`/v/api/vuukle/${userEmail}`)
            .get()
            .text(val => val)
            .then(function(response) {
              const profile = JSON.stringify({
                username: username,
                email: userEmail,
                public_key: get(global.qtConfig, ["vuukle", "public_key"]) || "",
                signature: response
              });
              const signature = window.btoa(profile);
              get(window, ["vuukleLogin"]) && window.vuukleLogin(signature);
            });
      })
      .catch(() => {
        console.warn("User attribution error occured");
      });
  },
  loadScript(onload, vikatanHosts) {
    let script1, script2;
    if (!this.scriptLoaded) {
      script1 = document.createElement("script");
      script1.type = "text/javascript";
      //  script1.defer = true;
      script1.src = "https://cdn.vuukle.com/platform.js";
      script2 = document.createElement("script");
      script2.innerHTML = `var VUUKLE_CONFIG={host: "vikatan.com","emotes": {
        "firstName": "ஹாப்பி",
        "fourthName": "லவ்",
        "fifthName": "கோபம்",
        "sixthName": "வருத்தம்",
        disable: [2, 3],
        "fourthImg" : "https://${
          global.qtConfig["cdn-image"]
        }/vikatan/2019-11/6d234603-1a98-4ba1-9d75-0a890fd36e42/loveemoji.svg"
    },apiKey:"${get(global.qtConfig, ["vuukle", "public_key"]) || ""}",comments: { transliteration:{
          language: "ta",
          enabledByDefault: true,
          }, auth: { password: false,vuukle: true,sso: { onClick : function() { return window.location.replace("${
            vikatanHosts.login
          }?redirect_url=${global.location.href}") }   } } } ,language:"ta",endlessMode:!0};`;
      const node = document.getElementsByTagName("script")[0];
      node.parentNode.insertBefore(script2, node);
      node.parentNode.insertBefore(script1, node);
      this.scriptLoaded = true;
      onload();
    } else {
      onload();
    }
  },
  register(story, vikatanHosts) {
    const that = this;
    this.loadScript(() => {
      let script;
      script = document.createElement("script");
      let id = story.id;
      // var newStr = id.replace(/-/g, "");
      const vuukleInitializerListenerId = setInterval(function() {
        if (window.VUUKLE_PLATFORM === 1) {
          clearInterval(vuukleInitializerListenerId);
          window.removeVuukleWidgets(id);
          const ele = document.getElementById(`vuukle-widget-${id}`);
          script.innerHTML = `window.newVuukleWidgets({elementsIndex:"${id}",articleId:"${id}",img:"https://${
            global.qtConfig["cdn-image"]
          }/${story["hero-image-s3-key"]}",title:"${story.headline
            .replace(/"/g, '\\"')
            .replace(/(\r\n|\n|\r)/gm, "")}",tags:"",url:"${story.url}"});`;
          if (typeof ele !== "undefined" && ele != null) {
            ele.appendChild(script);
          }
          that.registerUser();
        }
      });
    }, vikatanHosts);
  }
};

export const DailyMotion = {
  inited: false,
  autoplay: true,
  loadScript({ story }) {
    if (this.inited) {
      this.autoplay = false;
      this.cpe();
      return;
    }
    let e = window.document.createElement("script");
    e.type = "text/javascript";
    e.charset = "utf-8";
    e.setAttribute("crossorigin", "anonymous");
    e.defer = true;
    e.innerHTML =
      '(function(w,d,s,u,n,i,f,g,e,c){w.WDMObject=n;w[n]=w[n]||function(){(w[n].q=w[n].q||[]).push(arguments);};w[n].l=1*new Date();w[n].i=i;w[n].f=f;w[n].g=g;e=d.createElement(s);e.async=1;e.src=u;c=d.getElementsByTagName(s)[0];c.parentNode.insertBefore(e,c);})(window,document,"script","//api.dmcdn.net/pxl/cpe/client.min.js","cpe","6013d5ff38da30747958d161");window.addEventListener("cpepipclose", ({ detail: { player } }) => {player.pause();});';
    let t = window.document.getElementsByTagName("head")[0];
    t.appendChild(e);
    this.inited = true;
  },
  cpe() {
    const DMInitializerListenerId = setInterval(function() {
      if (window.cpe) {
        clearInterval(DMInitializerListenerId);
        window.cpe.parse();
      }
    });
  }
};

const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return "tablet";
  }
  if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return "mobile";
  }
  return "desktop";
};

const getOS = () => {
  var Name = "Unknown OS";
  if (navigator.userAgent.indexOf("Win") !== -1) Name = "Windows OS";
  if (navigator.userAgent.indexOf("Mac") !== -1) Name = "Macintosh";
  if (navigator.userAgent.indexOf("Linux") !== -1) Name = "Linux OS";
  if (navigator.userAgent.indexOf("Android") !== -1) Name = "Android OS";
  if (navigator.userAgent.indexOf("like Mac") !== -1) Name = "iOS";
  return Name;
};
